import { faXmark } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { Formik, getIn } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Accordion, Button, Card, Offcanvas } from "react-bootstrap";

import {
  TDSCreditRelatingToOptions,
  TypeOfIncomeOptions
} from "../../../../config/filingConstants";
import { INonSalaryTDS } from "../../../../config/interfaces";
import {
  NonSalaryTDSFormInitValues,
  NonSalaryTDSFormSchema
} from "../../../../pages/itr-filing/efiling.config";
import { useAppSelector } from "../../../../redux/Store";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import ScrollToFirstError from "../../../ScrollToFirstError";
import { SwitchBtnContextAwareToggle } from "../../../accordion";
import { FormField, NumericInputField, SelectInputField, TextInputField } from "../../../fields";

type Props = {
  showCanvas: boolean;
  formValues: INonSalaryTDS | null;
  handleCloseBtnPress: (salaryData: INonSalaryTDS | null) => void;
};

const TDSNonSalaryDetailsAddManuallyForm: React.FC<Props> = (props: Props) => {
  const { showCanvas, handleCloseBtnPress, formValues } = props;

  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const [initFormValues, setInitFormValues] = useState<INonSalaryTDS>(NonSalaryTDSFormInitValues);

  useEffect(() => {
    if (showCanvas && formValues) {
      setInitFormValues(() => cloneDeep(formValues));
    } else {
      setInitFormValues(() => cloneDeep(NonSalaryTDSFormInitValues));
    }
  }, [showCanvas, formValues]);

  return (
    <>
      <Offcanvas
        show={showCanvas}
        onHide={() => handleCloseBtnPress(null)}
        placement="end"
        backdrop={true}
      >
        <Formik
          enableReinitialize={true}
          initialValues={initFormValues}
          validationSchema={NonSalaryTDSFormSchema}
          onSubmit={(values) => handleCloseBtnPress(values)}
        >
          {({
            handleSubmit,
            handleBlur,
            setFieldValue,
            submitCount,
            isValid,
            errors,
            values,
            touched
          }) => (
            <>
              <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

              <Offcanvas.Header>
                <Offcanvas.Title>TDS - Non Salary</Offcanvas.Title>
                <Button
                  variant="primary"
                  className="icon-only hide-offcanvas"
                  onClick={() => handleCloseBtnPress(null)}
                >
                  <FASvgIcon icon={faXmark} />
                </Button>
              </Offcanvas.Header>

              <Offcanvas.Body>
                <section className="mb-5">
                  <div className="section-title-wrap">
                    <h5>
                      Tax Deduction at source
                      <small>
                        Provide this information from TDS certificate provided by deductor
                      </small>
                    </h5>
                  </div>
                  <FormField label="TAN of Deductor" isRequired={true} sm={6}>
                    <TextInputField
                      name={`DeductorTAN`}
                      value={values.DeductorTAN}
                      onChange={(value) => setFieldValue(`DeductorTAN`, value.toUpperCase())}
                      onBlur={handleBlur(`DeductorTAN`)}
                      isInvalid={
                        Boolean(getIn(errors, `DeductorTAN`)) && getIn(touched, `DeductorTAN`)
                      }
                      errorMsg={getIn(errors, `DeductorTAN`)}
                      note="Enter 'GGGGG0000G' if you have donated to Government Funds that don't have a PAN"
                    />
                  </FormField>

                  <FormField label="Name of Deductor" isRequired={true} sm={6}>
                    <TextInputField
                      name={`DeductorName`}
                      value={values.DeductorName}
                      onChange={(value) => setFieldValue(`DeductorName`, value)}
                      onBlur={handleBlur(`DeductorName`)}
                      isInvalid={
                        Boolean(getIn(errors, `DeductorName`)) && getIn(touched, `DeductorName`)
                      }
                      errorMsg={getIn(errors, `DeductorName`)}
                    />
                  </FormField>

                  <FormField label="Total Tax Deducted" isRequired={true} sm={6}>
                    <NumericInputField
                      showLeftIcon={true}
                      name="TotalTaxDeducted"
                      value={values.TotalTaxDeducted}
                      onChange={(value) => setFieldValue("TotalTaxDeducted", Number(value))}
                      onBlur={handleBlur("TotalTaxDeducted")}
                      isInvalid={Boolean(errors.TotalTaxDeducted) && touched.TotalTaxDeducted}
                      errorMsg={errors.TotalTaxDeducted}
                      decimalUpto={0}
                      note="Leave it blank if not deducted in the name of assessee."
                    />
                  </FormField>
                </section>

                <section className="mb-5">
                  <Accordion flush activeKey={values.ClaimOrTransferTDSCredits ? "0" : "1"}>
                    <Card className="more-info-card shadow-none overflow-hidden">
                      <Card.Header
                        className={classNames(!values.ClaimOrTransferTDSCredits && "border-0")}
                      >
                        <Card.Title>
                          <span>Claim or Transfer TDS credits from another assessee</span>
                          <SwitchBtnContextAwareToggle
                            eventKey="0"
                            name="ClaimOrTransferTDSCredits"
                            onChange={(checked: boolean) =>
                              setFieldValue("ClaimOrTransferTDSCredits", checked)
                            }
                          />
                        </Card.Title>
                      </Card.Header>
                      <Accordion.Collapse eventKey="0">
                        <Card.Body className="pb-0">
                          <section>
                            <FormField label="TDS Credit relating to" isRequired={true} sm={6}>
                              <SelectInputField
                                placeholder="Select"
                                name={`TDSCreditRelatingTo`}
                                isOptionArrayOfObject={false}
                                options={TDSCreditRelatingToOptions}
                                value={[values.TDSCreditRelatingTo]}
                                onChange={(selectedOption) => {
                                  if (selectedOption) {
                                    setFieldValue(`TDSCreditRelatingTo`, selectedOption.value);
                                  } else {
                                    setFieldValue(`TDSCreditRelatingTo`, undefined);
                                  }
                                }}
                                onBlur={handleBlur(`TDSCreditRelatingTo`)}
                                isInvalid={
                                  Boolean(getIn(errors, `TDSCreditRelatingTo`)) &&
                                  getIn(touched, `TDSCreditRelatingTo`)
                                }
                                errorMsg={getIn(errors, `TDSCreditRelatingTo`)}
                              />
                            </FormField>

                            <FormField label="TDS Credit" isRequired={true} sm={6}>
                              <NumericInputField
                                showLeftIcon={true}
                                name="TDSCreditAmount"
                                value={values.TDSCreditAmount}
                                onChange={(value) => setFieldValue("TDSCreditAmount", value)}
                                onBlur={handleBlur("TDSCreditAmount")}
                                isInvalid={
                                  Boolean(getIn(errors, `TDSCreditAmount`)) &&
                                  getIn(touched, `TDSCreditAmount`)
                                }
                                errorMsg={getIn(errors, `TDSCreditAmount`)}
                                note="Amount of TDS received or transferred."
                              />
                            </FormField>

                            <FormField label="PAN of Other Person" isRequired={true} sm={6}>
                              <TextInputField
                                name={`PANOfOtherPerson`}
                                value={values.PANOfOtherPerson}
                                onChange={(value) =>
                                  setFieldValue(`PANOfOtherPerson`, value.toUpperCase())
                                }
                                onBlur={() => handleBlur(`PANOfOtherPerson`)}
                                isInvalid={
                                  Boolean(getIn(errors, `PANOfOtherPerson`)) &&
                                  getIn(touched, `PANOfOtherPerson`)
                                }
                                errorMsg={getIn(errors, `PANOfOtherPerson`)}
                              />
                            </FormField>

                            <FormField
                              label="Income relating to TDS Credit"
                              isRequired={true}
                              sm={6}
                            >
                              <NumericInputField
                                showLeftIcon={true}
                                name="IncomeRelatingToTDSCredit"
                                value={values.IncomeRelatingToTDSCredit}
                                onChange={(value) =>
                                  setFieldValue("IncomeRelatingToTDSCredit", Number(value))
                                }
                                onBlur={() => handleBlur("IncomeRelatingToTDSCredit")}
                                isInvalid={
                                  Boolean(errors.IncomeRelatingToTDSCredit) &&
                                  touched.IncomeRelatingToTDSCredit
                                }
                                errorMsg={errors.IncomeRelatingToTDSCredit}
                                decimalUpto={0}
                              />
                            </FormField>
                          </section>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                </section>

                <section className="mb-5">
                  <FormField label="Tax Deducted claimed for this year" isRequired={true} sm={6}>
                    <NumericInputField
                      showLeftIcon={true}
                      name="TaxDeductedClaimedForThisYear"
                      value={values.TaxDeductedClaimedForThisYear}
                      onChange={(value) =>
                        setFieldValue("TaxDeductedClaimedForThisYear", Number(value))
                      }
                      onBlur={handleBlur("TaxDeductedClaimedForThisYear")}
                      isInvalid={
                        Boolean(errors.TaxDeductedClaimedForThisYear) &&
                        touched.TaxDeductedClaimedForThisYear
                      }
                      errorMsg={errors.TaxDeductedClaimedForThisYear}
                      decimalUpto={0}
                      note="Usually this amount is same as Total Tax Deducted."
                    />
                  </FormField>

                  <FormField label="Income against which TDS was deducted" isRequired={true} sm={6}>
                    <NumericInputField
                      showLeftIcon={true}
                      name="IncomeAgainstTDS"
                      value={values.IncomeAgainstTDS}
                      onBlur={handleBlur("IncomeAgainstTDS")}
                      onChange={(value) => setFieldValue("IncomeAgainstTDS", Number(value))}
                      isInvalid={Boolean(errors.IncomeAgainstTDS) && touched.IncomeAgainstTDS}
                      errorMsg={errors.IncomeAgainstTDS}
                      decimalUpto={0}
                    />
                  </FormField>

                  <FormField label="Type of Income" isRequired={true} sm={6}>
                    <SelectInputField
                      placeholder="Select"
                      name={`TypeOfIncome`}
                      options={UtilService.convertToArray(TypeOfIncomeOptions)}
                      value={{
                        "label": TypeOfIncomeOptions[values.TypeOfIncome],
                        "value": values.TypeOfIncome
                      }}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setFieldValue(`TypeOfIncome`, selectedOption.value);
                        } else {
                          setFieldValue(`TypeOfIncome`, undefined);
                        }
                      }}
                      onBlur={handleBlur(`TypeOfIncome`)}
                      isInvalid={
                        Boolean(getIn(errors, `TypeOfIncome`)) && getIn(touched, `TypeOfIncome`)
                      }
                      errorMsg={getIn(errors, `TypeOfIncome`)}
                    />
                  </FormField>

                  <FormField
                    label="Financial Year in which TDS was deducted"
                    isRequired={true}
                    sm={6}
                  >
                    <SelectInputField
                      placeholder="Select"
                      name={`FinancialYearTDSDeducted`}
                      isOptionArrayOfObject={false}
                      options={UtilService.generateYearRanges({ start: 2011 })}
                      value={[values.FinancialYearTDSDeducted]}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setFieldValue(`FinancialYearTDSDeducted`, selectedOption.value);
                        } else {
                          setFieldValue(`FinancialYearTDSDeducted`, undefined);
                        }
                      }}
                      onBlur={handleBlur(`FinancialYearTDSDeducted`)}
                      isInvalid={
                        Boolean(getIn(errors, `FinancialYearTDSDeducted`)) &&
                        getIn(touched, `FinancialYearTDSDeducted`)
                      }
                      errorMsg={getIn(errors, `FinancialYearTDSDeducted`)}
                      note="This is the same as financial year of the return (2022-23)"
                    />
                  </FormField>
                </section>
              </Offcanvas.Body>

              <div className="offcanvas-btns">
                {!isFilingReadOnly ? (
                  <Button variant="outline-white" size="sm" onClick={() => handleSubmit()}>
                    Save and Close
                  </Button>
                ) : (
                  <Button
                    variant="outline-white"
                    size="sm"
                    onClick={() => handleCloseBtnPress(null)}
                  >
                    Close
                  </Button>
                )}
              </div>
            </>
          )}
        </Formik>
      </Offcanvas>
    </>
  );
};

export default TDSNonSalaryDetailsAddManuallyForm;
